import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AccessControlAPI } from "../../api/accessControl";

export default function AccessControlUserDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [linkForm, setLinkForm] = useState({
    nightjarUserId: "",
    email: "",
  });
  const [linkSaving, setLinkSaving] = useState(false);
  const [linkMessage, setLinkMessage] = useState("");
  const [tokenForm, setTokenForm] = useState({ tokenNumber: "", tokenType: "fob" });
  const [tokenSaving, setTokenSaving] = useState(false);
  const [tokenMessage, setTokenMessage] = useState("");
  const [accessLevels, setAccessLevels] = useState([]);
  const [userAccess, setUserAccess] = useState([]);
  const [accessSaving, setAccessSaving] = useState(false);

  async function load() {
    try {
      setLoading(true);
      setError("");

      const [userData, tokenData, accessData, levelsData] = await Promise.all([
        AccessControlAPI.getUser(id),
        AccessControlAPI.listUserTokens(id),
        AccessControlAPI.getUserAccess(id),
        AccessControlAPI.listAccessLevels(user?.system_id || 0),
      ]);

      setUser(userData);
      setTokens(Array.isArray(tokenData) ? tokenData : []);
      setLinkForm({
        nightjarUserId: userData?.nightjar_user_id || "",
        email: userData?.email || "",
      });
      setUserAccess(Array.isArray(accessData) ? accessData : []);
      setAccessLevels(Array.isArray(levelsData) ? levelsData : []);
    } catch (err) {
      console.error("Failed to load access control user detail", err);
      setError("Failed to load Net2 user details");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [id]);

  function getDisplayName() {
    if (!user) return "Net2 User";

    const parts = [user.first_name, user.surname].filter(Boolean);

    if (parts.length > 0) {
      return parts.join(" ");
    }

    return user.email || `Net2 user ${user.net2_user_id}`;
  }

  async function handleLinkSubmit(e) {
    e.preventDefault();

    try {
      setLinkSaving(true);
      setError("");
      setLinkMessage("");

      await AccessControlAPI.linkUser({
        id,
        nightjarUserId: Number(linkForm.nightjarUserId),
        email: linkForm.email.trim(),
      });

      setLinkMessage("User link job created successfully.");
      await load();
    } catch (err) {
      console.error("Failed to link access control user", err);
      setError("Failed to link Net2 user to Nightjar user");
    } finally {
      setLinkSaving(false);
    }
  }

  async function handleAddToken(e) {
    e.preventDefault();
    try {
      setTokenSaving(true);
      setError("");
      setTokenMessage("");
      const result = await AccessControlAPI.addUserToken({
        id,
        tokenNumber: tokenForm.tokenNumber.trim(),
        tokenType: tokenForm.tokenType,
      });
      setTokenMessage(`Add token jobs created: ${result.jobs?.length || 0}`);
      setTokenForm({ tokenNumber: "", tokenType: "fob" });
      await load();
    } catch (err) {
      console.error("Failed to add token", err);
      setError("Failed to add token");
    } finally {
      setTokenSaving(false);
    }
  }

  async function handleRemoveToken(tokenNumber) {
    try {
      setTokenSaving(true);
      setError("");
      setTokenMessage("");
      const result = await AccessControlAPI.removeUserToken({ id, tokenNumber });
      setTokenMessage(`Remove token jobs created: ${result.jobs?.length || 0}`);
      await load();
    } catch (err) {
      console.error("Failed to remove token", err);
      setError("Failed to remove token");
    } finally {
      setTokenSaving(false);
    }
  }

  async function handleSetAccess(systemId, accessLevelId) {
    try {
      setAccessSaving(true);
      setError("");

      await AccessControlAPI.setUserAccess({
        id,
        systemId,
        accessLevelId,
      });

      await load();
    } catch (err) {
      console.error("Failed to set access level", err);
      setError("Failed to update access level");
    } finally {
      setAccessSaving(false);
    }
  }

  if (loading) {
    return <div className="p-6 text-sm text-gray-600">Loading...</div>;
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      </div>
    );
  }

  if (!user) {
    return <div className="p-6 text-sm text-gray-600">User not found.</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <button
            type="button"
            onClick={() => navigate("/admin/access-control/users")}
            className="mb-3 text-sm text-blue-600 hover:underline"
          >
            ← Back to Net2 users
          </button>

          <h1 className="text-2xl font-semibold">{getDisplayName()}</h1>
          <p className="text-sm text-gray-600">
            Synced Net2 user details and tokens.
          </p>
        </div>

        <button
          type="button"
          onClick={load}
          className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700"
        >
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="rounded border bg-white p-4 space-y-3">
          <h2 className="font-medium">Net2 User</h2>

          <dl className="text-sm">
            <div className="grid grid-cols-3 gap-2 py-1">
              <dt className="font-medium text-gray-600">Name</dt>
              <dd className="col-span-2">{getDisplayName()}</dd>
            </div>

            <div className="grid grid-cols-3 gap-2 py-1">
              <dt className="font-medium text-gray-600">Email</dt>
              <dd className="col-span-2">{user.email || "-"}</dd>
            </div>

            <div className="grid grid-cols-3 gap-2 py-1">
              <dt className="font-medium text-gray-600">School</dt>
              <dd className="col-span-2">{user.school_name || user.school_id || "-"}</dd>
            </div>

            <div className="grid grid-cols-3 gap-2 py-1">
              <dt className="font-medium text-gray-600">System</dt>
              <dd className="col-span-2">{user.system_name || user.system_id || "-"}</dd>
            </div>

            <div className="grid grid-cols-3 gap-2 py-1">
              <dt className="font-medium text-gray-600">Net2 ID</dt>
              <dd className="col-span-2">{user.net2_user_id}</dd>
            </div>

            <div className="grid grid-cols-3 gap-2 py-1">
              <dt className="font-medium text-gray-600">Last synced</dt>
              <dd className="col-span-2">
                {user.last_synced_at
                  ? new Date(user.last_synced_at).toLocaleString()
                  : "-"}
              </dd>
            </div>
          </dl>
        </div>

        <div className="rounded border bg-white p-4 space-y-3">
          <h2 className="font-medium">Nightjar Link</h2>
          <p className="text-sm text-gray-600">
            Link this Net2 user to a Nightjar user. This creates an UpdateUser
            job and updates the Net2 email address through the webhook.
          </p>

          {linkMessage && (
            <div className="rounded border border-green-300 bg-green-50 p-2 text-sm text-green-700">
              {linkMessage}
            </div>
          )}

          <div className="text-sm">
            <span className="font-medium text-gray-600">Current Nightjar user ID: </span>
            {user.nightjar_user_id || "Not linked"}
          </div>

          <form onSubmit={handleLinkSubmit} className="space-y-3">
            <div>
              <label className="block text-sm font-medium mb-1">
                Nightjar User ID
              </label>
              <input
                type="number"
                value={linkForm.nightjarUserId}
                onChange={(e) =>
                  setLinkForm({ ...linkForm, nightjarUserId: e.target.value })
                }
                className="w-full rounded border p-2"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Email address to set in Net2
              </label>
              <input
                type="email"
                value={linkForm.email}
                onChange={(e) =>
                  setLinkForm({ ...linkForm, email: e.target.value })
                }
                className="w-full rounded border p-2"
                required
              />
            </div>

            <button
              type="submit"
              disabled={linkSaving}
              className="rounded bg-blue-600 px-4 py-2 text-sm text-white disabled:opacity-60"
            >
              {linkSaving ? "Linking..." : "Link User"}
            </button>
          </form>
        </div>
      </div>

      <div className="rounded border bg-white">
        <div className="border-b p-3">
          <h2 className="font-medium">Tokens</h2>
        </div>

        <div className="border-b p-4 space-y-3">
          <p className="text-sm text-gray-600">
            Adding or removing a token creates jobs for every synced Net2 user with the same email.
          </p>

          {tokenMessage && (
            <div className="rounded border border-green-300 bg-green-50 p-2 text-sm text-green-700">
              {tokenMessage}
            </div>
          )}

          <form onSubmit={handleAddToken} className="grid grid-cols-1 gap-3 md:grid-cols-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-1">Token Number</label>
              <input
                type="text"
                value={tokenForm.tokenNumber}
                onChange={(e) => setTokenForm({ ...tokenForm, tokenNumber: e.target.value })}
                className="w-full rounded border p-2"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Token Type</label>
              <select
                value={tokenForm.tokenType}
                onChange={(e) => setTokenForm({ ...tokenForm, tokenType: e.target.value })}
                className="w-full rounded border p-2"
              >
                <option value="fob">Fob</option>
                <option value="card">Card</option>
              </select>
            </div>

            <div className="flex items-end">
              <button
                type="submit"
                disabled={tokenSaving || !user.email}
                className="w-full rounded bg-blue-600 px-4 py-2 text-sm text-white disabled:opacity-60"
              >
                {tokenSaving ? "Creating..." : "Add Token"}
              </button>
            </div>
          </form>

          {!user.email && (
            <div className="text-sm text-amber-700">
              This user needs an email before tokens can be managed.
            </div>
          )}
        </div>

        {tokens.length === 0 ? (
          <div className="p-4 text-sm text-gray-600">No tokens found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b bg-gray-50">
                <tr>
                  <th className="p-3">Token Number</th>
                  <th className="p-3">Net2 Token ID</th>
                  <th className="p-3">Email</th>
                  <th className="p-3">Last Synced</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {tokens.map((token) => (
                  <tr key={token.id} className="border-b">
                    <td className="p-3 font-medium">{token.token_number}</td>
                    <td className="p-3">{token.net2_token_id}</td>
                    <td className="p-3">{token.email || "-"}</td>
                    <td className="p-3">
                      {token.last_synced_at
                        ? new Date(token.last_synced_at).toLocaleString()
                        : "-"}
                    </td>
                    <td className="p-3 text-right">
                      <button
                        type="button"
                        disabled={tokenSaving}
                        onClick={() => handleRemoveToken(token.token_number)}
                        className="text-sm text-red-600 hover:underline disabled:opacity-60"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="rounded border bg-white p-4 space-y-3">
        <h2 className="font-medium">Access Levels</h2>

        {userAccess.length === 0 ? (
          <div className="text-sm text-gray-600">No access levels assigned.</div>
        ) : (
          <div className="space-y-2">
            {userAccess.map((row) => (
              <div key={row.system_id} className="flex items-center gap-3">
                <div className="w-48 text-sm font-medium">
                  {row.system_name}
                </div>

                <select
                  value={row.access_level_id}
                  onChange={(e) =>
                    handleSetAccess(row.system_id, Number(e.target.value))
                  }
                  disabled={accessSaving}
                  className="rounded border p-2 text-sm"
                >
                  {accessLevels.map((level) => (
                    <option key={level.id} value={level.id}>
                      {level.name}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded border bg-white p-4">
        <h2 className="font-medium mb-2">Raw Net2 Data</h2>
        <pre className="max-h-96 overflow-auto rounded bg-gray-50 p-3 text-xs">
          {JSON.stringify(user.raw_data || {}, null, 2)}
        </pre>
      </div>
    </div>
  );
}